import * as t from 'io-ts';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { logger } from '@main/core-ui';
import type { DataSubject } from '@main/datamap-types';
import { isOauthSubject } from '@main/datamap-types';
import { ImmutableUrl } from '@main/immutable-url';

/**
 * Create a random state, useful for verifying that initiation of a request.
 *
 * i.e.
 * - generate random state
 * - send request
 * - check state is returned to verify origin
 *
 * @returns The generated random value
 */
function generateRandomState(): string {
  const bytes = new Uint8Array(16);

  // Populate array with random values.
  crypto.getRandomValues(bytes);

  // Encode as base64, removing trailing '='
  return btoa(String.fromCharCode(...bytes)).replace(/=+$/, '');
}

/**
 * OAuth state shape
 */
export const OAuthState = t.type({
  /** Random state */
  randomState: t.string,
  /** Pathname to redirect to */
  pathname: t.string,
});

/** Override type */
export type OAuthState = t.TypeOf<typeof OAuthState>;

/**
 * URL/random state for redirect
 */
interface AuthRedirect {
  /** The URL to redirect to */
  url: ImmutableUrl;
  /** The randomly generated state */
  state?: string;
}

export interface OAuthAuthorizationUrlInput {
  /** URL to redirect to to start oauth loop */
  redirectUrl: string;
  /** OAuth client ID */
  oauthClientId: string;
  /** OAuth scope */
  oauthScope: string | undefined;
}

/**
 * Assembles the necessary components into an authorization url.
 *
 * @param input - The oauth URL creation input
 * @param state - The oauth2 state to pass in URL
 * @returns The authorization URL
 */
export function generateOAuthAuthorizationUrl(
  { redirectUrl, oauthScope, oauthClientId }: OAuthAuthorizationUrlInput,
  state: OAuthState,
): ImmutableUrl {
  const url = new ImmutableUrl(redirectUrl);
  return url.transform({
    searchTransform: (oldQs) => ({
      ...oldQs,
      scope: oauthScope,
      response_type: 'code',
      client_id: oauthClientId,
      redirect_uri: `${window.location.origin}/oauth-callback`,
      state: JSON.stringify(state),
    }),
  });
}

/**
 * Create an authentication URL from a data subject configuration
 *
 * @param activeSubject - The data subject
 * @param redirectPathname - Pathname to redirect to
 * @returns The authorization URL that the data subject will navigate to in order to login
 */
export function useAuthorizationUrl(
  activeSubject?: DataSubject,
  redirectPathname?: string,
): AuthRedirect | undefined {
  const { pathname } = useLocation();
  const { formatMessage } = useIntl();
  // Need a data subject to create the URL
  if (!activeSubject) {
    return undefined;
  }

  try {
    // Generate oauth URL
    if (isOauthSubject(activeSubject)) {
      // Generate random state and write to local storage
      const randomState = generateRandomState();
      const redirectTo = redirectPathname || pathname;
      const url = generateOAuthAuthorizationUrl(
        {
          oauthClientId: activeSubject.oauthClientId,
          oauthScope: activeSubject.oauthScope,
          redirectUrl: formatMessage(activeSubject.redirectUrl),
        },
        {
          pathname: redirectTo,
          randomState,
        },
      );
      return { url, state: randomState };
    }

    // Need a redirect url
    if (!activeSubject.redirectUrl) {
      return undefined;
    }

    // Fallback to magic link redirect
    return {
      url: new ImmutableUrl(formatMessage(activeSubject.redirectUrl)),
    };
  } catch (err) {
    logger.error(
      `Failed to format redirect URL for subject ${activeSubject?.id}: ${activeSubject.redirectUrl}`,
    );
    return undefined;
  }
}
